import { create } from 'zustand';
import type { LeadsViewMode, LeadStatus } from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';

export type LeadsSort = 'name' | 'contact' | 'category' | 'recent' | 'status' | 'nextContact' | 'website' | 'city';
export type LeadsDueFilter = 'all' | 'due' | 'scheduled' | 'none';
export type LeadsCityFilter = 'all' | 'missing' | string;
export type LeadEditorTab = 'general' | 'contact' | 'conversion';

interface LeadSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
}

function createInitialSelectionSession(): LeadSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0
  };
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getCurrentWeekStartDate(): string {
  const today = new Date();
  const day = today.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  today.setDate(today.getDate() - daysFromMonday);
  today.setHours(12, 0, 0, 0);
  return toDateInputValue(today);
}

interface LeadsViewStore {
  statusFilter: 'all' | LeadStatus;
  categoryFilter: 'all' | 'unassigned' | string;
  cityFilter: LeadsCityFilter;
  dueFilter: LeadsDueFilter;
  sort: LeadsSort;
  plannerWeekStart: string;
  selectedPlannerDate: string;
  editorTab: LeadEditorTab;
  selectionSession: LeadSelectionSession;
  setStatusFilter: (value: 'all' | LeadStatus) => void;
  setCategoryFilter: (value: 'all' | 'unassigned' | string) => void;
  setCityFilter: (value: LeadsCityFilter) => void;
  setDueFilter: (value: LeadsDueFilter) => void;
  setSort: (value: LeadsSort) => void;
  setPlannerWeekStart: (value: string) => void;
  setSelectedPlannerDate: (value: string) => void;
  setEditorTab: (value: LeadEditorTab) => void;
  setViewMode: (value: LeadsViewMode) => Promise<void>;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
}

export const useLeadsViewStore = create<LeadsViewStore>((set) => ({
  statusFilter: 'all',
  categoryFilter: 'all',
  cityFilter: 'all',
  dueFilter: 'all',
  sort: 'nextContact',
  plannerWeekStart: getCurrentWeekStartDate(),
  selectedPlannerDate: toDateInputValue(new Date()),
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCategoryFilter: (categoryFilter) => set({ categoryFilter }),
  setCityFilter: (cityFilter) => set({ cityFilter }),
  setDueFilter: (dueFilter) => set({ dueFilter }),
  setSort: (sort) => set({ sort }),
  setPlannerWeekStart: (plannerWeekStart) => set({ plannerWeekStart }),
  setSelectedPlannerDate: (selectedPlannerDate) => set({ selectedPlannerDate }),
  setEditorTab: (editorTab) => set({ editorTab }),
  setViewMode: async (leadsViewMode) => {
    await useShellStore.getState().savePreferences({ leadsViewMode });
  },
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    }))
}));
